import { composeClassNames } from "@faedev/editor-utils";
import MuxPlayer from "@mux/mux-player-react";
import { useState } from "react";
import {
  Image,
  OverlayTrigger,
  Popover,
  PopoverContent,
} from "react-bootstrap";
import Text from "src/components/Text";
import VideoPlayer from "src/components/VideoPlayer";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import validateAuthToken from "src/functions/validateAuthToken";
import Play from "src/icons/Play";
import { sMuxThumbnail } from "src/styles/sMuxThumbnail.module.scss";
import { sPopover } from "src/styles/sPopover.module.scss";
import { sVideo } from "src/styles/sVideo.module.scss";
import { ApiLectures } from "src/types/ApiLectures";
import { ApiPrograms } from "src/types/ApiPrograms";
import Translate from "./Translate";

type Props = {
  className?: (string | string) | null | undefined;
  data: ApiLectures | ApiPrograms;
  videoMuxPaid: boolean;
  videoDefined: boolean;
  clientId: string;
};

function Video({
  className,
  data,
  videoMuxPaid,
  videoDefined,
  clientId,
}: Props) {
  const [tokens, setTokens] = useState<any>();

  const popover = (
    <Popover
      id="popover-alert"
      className={composeClassNames([sPopover, "popover-warning"])}
    >
      <PopoverContent>
        <Text width={`semibold`} size="xn">
          <Translate
            tKey={`You need to purchase a class/serie to play the video.`}
          />
        </Text>
      </PopoverContent>
    </Popover>
  );

  const loadMux = async () => {
    const response = await fetch(
      constants.Paths.apiUrl + `/get-jwt/` + data.video.mux.playback_id,
      {
        method: "GET",
        headers: validateAuthToken(getCookieByName(`auth-token`)),
      }
    );

    const responseTokens = await response.json();
    setTokens(responseTokens);
  };

  const thumbnailFromStrapi = typeof data.thumbnail?.url === "string";
  const thubmnailFromMux = `${constants.Paths.muxImage}${data.video?.mux?.playback_id}/thumbnail.jpg?token=${data.video?.mux?.thumbnail_token}`;
  const thumbnail = thumbnailFromStrapi ? data.thumbnail.url : thubmnailFromMux;
  const gifFromMux = `${constants.Paths.muxImage}${data.video?.mux?.playback_id}/animated.gif?width=640&token=${data.video?.mux?.gif_token}`;

  const [active, setActive] = useState(thumbnail);

  if (!data.video) {
    return (
      <div className={composeClassNames([sMuxThumbnail, "mux-thumbnail"])}>
        <Image
          className="mux-thumbnail-image"
          src={data.thumbnail?.url}
          alt={data.title}
        />
      </div>
    );
  }

  return (
    <div className={composeClassNames([sVideo, className, "video-wrapper"])}>
      {!videoMuxPaid && videoDefined && (
        <VideoPlayer data={data.video} thumbnail={active} />
      )}
      {!videoMuxPaid && !videoDefined && (
        <div
          className={composeClassNames([sMuxThumbnail, "mux-thumbnail"])}
          onMouseEnter={() => setActive(gifFromMux)}
          onMouseLeave={() => setActive(thumbnail)}
        >
          <Image
            className="mux-thumbnail-image"
            src={active}
            alt={data.title}
          />
          <OverlayTrigger trigger="click" placement="top" overlay={popover}>
            <Play className="mux-thumbnail-play-button" />
          </OverlayTrigger>
        </div>
      )}
      {videoMuxPaid && !tokens && (
        <div
          className={composeClassNames([sMuxThumbnail, "mux-thumbnail"])}
          onClick={() => loadMux()}
        >
          <Image
            className="mux-thumbnail-image"
            src={active}
            alt={data.title}
          />
          <Play className="mux-thumbnail-play-button" />
        </div>
      )}
      {tokens && (
        <MuxPlayer
          envKey={constants.Api.MuxDataEnvKey}
          streamType="on-demand"
          playbackId={data.video.mux.playback_id}
          metadata={{
            video_id: `${data.content_type}-id-${data.video.id}`,
            video_title: data.video.title,
            viewer_user_id: clientId,
          }}
          tokens={{
            playback: tokens.video,
            storyboard: tokens.storyboard,
          }}
          autoPlay
        />
      )}
    </div>
  );
}

export default Video;
